import { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import { useSearchParams } from "react-router-dom";

import { PAGES_DOMAIN_URL } from "constants";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import PageList from "./PageList";

export default () => {
  const [searchParams] = useSearchParams();

  const [listPage, setPage] = useState(() => {
    const page = searchParams.get("page") ? searchParams.get("page") : 1;

    return Number(page);
  });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [confirmDuplicate, setConfirmDuplicate] = useState(null);
  const [loadingDuplicate, setLoadingDuplicate] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);

  const limit = 5;

  // Fetch data
  const GET_PAGE_LIST_DATA = gql`
    query GetPagesData($page: Int!, $limit: Int!) {
      pageListData(page: $page, limit: $limit) {
        pages {
          id
          code
          name
          list {
            id
            name
            behalf_name
            behalf_street
            behalf_email
            behalf_city
            behalf_zip
            behalf_country
          }
          redirect_link
          stats {
            id
            to
            total_leads
            total_leads_verified
            total_views
          }
          views {
            id
            created_at
          }
          leads {
            id
            created_at
            verified
          }
        }
        pageLists {
          id
          name
        }
        numOfResults
        loginCode
      }
    }
  `;

  const { loading, data, error, refetch } = useQuery(GET_PAGE_LIST_DATA, {
    variables: {
      page: listPage,
      limit,
    },
    fetchPolicy: "network-only",
    pollInterval: 43200000,
  });

  if (error) {
    console.log("error fetching page list data", error);
    captureException(error);
  }

  //console.log("data", data);

  // handle pagination
  if (data) {
    if (data.pageListData.numOfResults > 0)
      if (listPage > Math.ceil(data.pageListData.numOfResults / limit))
        setPage(1);
  }

  const paginate = (where) => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (where === "previous") setPage((prevState) => prevState - 1);
    if (where === "next") setPage((prevState) => prevState + 1);
  };

  // handle Page delete
  const onDeleteClick = (pageId) => {
    setConfirmDelete(pageId);
  };

  const closeDeleteModal = () => {
    setConfirmDelete(null);
  };

  const DELETE_PAGE = gql`
    mutation DeletePage($pageId: ID!) {
      deletePage(pageId: $pageId) {
        resultCode
      }
    }
  `;

  const [deletePage] = useMutation(DELETE_PAGE);

  const onConfirmDelete = async () => {
    try {
      if (!confirmDelete) return;

      if (loadingDelete) return;

      setLoadingDelete(true);

      await deletePage({
        variables: {
          pageId: confirmDelete,
        },
      });

      refetch();

      setLoadingDelete(false);
      setConfirmDelete(null);
    } catch (error) {
      console.log("onConfirmDelete error", error);
      captureException(error);
      setLoadingDelete(false);
      return;
    }
  };

  // handle Page duplicate
  const onDuplicateClick = (pageId) => {
    setConfirmDuplicate(pageId);
  };

  const closeDuplicateModal = () => {
    setConfirmDuplicate(null);
  };

  const DUPLICATE_PAGE = gql`
    mutation DuplicatePage($pageId: ID!) {
      duplicatePage(pageId: $pageId) {
        resultCode
      }
    }
  `;

  const [duplicatePage] = useMutation(DUPLICATE_PAGE);

  const onConfirmDuplicate = async () => {
    try {
      if (!confirmDuplicate) return;

      if (loadingDuplicate) return;

      setLoadingDuplicate(true);

      const result = await duplicatePage({
        variables: {
          pageId: confirmDuplicate,
        },
      });

      if (result.data.duplicatePage.resultCode === 7) {
        setDuplicateError(
          "You can have a maximum of 20 active landing pages. Please delete an older page before creating a new one."
        );
        setConfirmDuplicate(null);
        setLoadingDuplicate(false);
        return;
      }

      refetch();

      setLoadingDuplicate(false);
      setConfirmDuplicate(null);
    } catch (error) {
      console.log("onConfirmDuplicate error", error);
      captureException(error);
      setLoadingDuplicate(false);
      return;
    }
  };

  const onCloseErrorModal = () => {
    setDuplicateError(null);
  };

  //console.log("data", data);

  return (
    <>
      {!data && (
        <LoadingBox>
          <BoxLoadingSpinner height="435px" />
        </LoadingBox>
      )}
      {data && (
        <PageList
          data={data}
          refetch={refetch}
          onDeleteClick={onDeleteClick}
          confirmDelete={confirmDelete}
          closeDeleteModal={closeDeleteModal}
          onConfirmDelete={onConfirmDelete}
          confirmDuplicate={confirmDuplicate}
          closeDuplicateModal={closeDuplicateModal}
          onDuplicateClick={onDuplicateClick}
          onConfirmDuplicate={onConfirmDuplicate}
          paginate={paginate}
          listPage={listPage}
          limit={limit}
          duplicateError={duplicateError}
          onCloseErrorModal={onCloseErrorModal}
        />
      )}
    </>
  );
};
